import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API } from "@/App";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { GraduationCap, Mail, Lock, ArrowLeft, Loader2, CheckCircle2 } from "lucide-react";

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // "email" | "reset" | "done"
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Veuillez saisir votre email");
      return;
    }

    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setToken(res.data.token);
      setStep("reset");
      toast.success("Email vérifié !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la vérification");
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmPassword) {
      toast.error("Veuillez remplir tous les champs");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Le mot de passe doit contenir au moins 6 caractères");
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error("Les mots de passe ne correspondent pas");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword,
      });
      setStep("done");
      toast.success("Mot de passe réinitialisé !");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur lors de la réinitialisation");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-container">
      <div className="w-full max-w-md px-4">
        <Link
          to="/auth?mode=login"
          className="inline-flex items-center gap-2 text-slate-600 hover:text-blue-900 mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Retour à la connexion
        </Link>

        <Card className="auth-card border-0">
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-2">
              <GraduationCap className="h-10 w-10 text-blue-900" />
              <span className="font-serif font-bold text-2xl text-blue-900">CiviquePrep</span>
            </div>
          </div>

          {step === "email" && (
            <CardContent className="space-y-4 p-0">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Mot de passe oublié</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Saisissez votre email pour réinitialiser votre mot de passe
                </p>
              </div>
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="forgot-email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="forgot-email"
                      type="email"
                      placeholder="jean@example.com"
                      className="pl-10"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      data-testid="forgot-email-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-900 hover:bg-blue-800"
                  disabled={loading}
                  data-testid="forgot-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Vérification...
                    </>
                  ) : (
                    "Vérifier mon email"
                  )}
                </Button>
              </form>
            </CardContent>
          )}

          {step === "reset" && (
            <CardContent className="space-y-4 p-0">
              <div className="text-center mb-4">
                <h2 className="text-xl font-semibold text-slate-900">Nouveau mot de passe</h2>
                <p className="text-sm text-slate-500 mt-1">
                  Choisissez un nouveau mot de passe pour votre compte
                </p>
              </div>
              <form onSubmit={handleResetSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="new-password">Nouveau mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="new-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      data-testid="reset-password-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-new-password">Confirmer le mot de passe</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="confirm-new-password"
                      type="password"
                      placeholder="••••••••"
                      className="pl-10"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      data-testid="reset-confirm-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-900 hover:bg-blue-800"
                  disabled={loading}
                  data-testid="reset-submit-btn"
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Réinitialisation...
                    </>
                  ) : (
                    "Réinitialiser mon mot de passe"
                  )}
                </Button>
              </form>
            </CardContent>
          )}

          {step === "done" && (
            <CardContent className="p-0">
              <div className="text-center space-y-4" data-testid="reset-success">
                <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-semibold text-slate-900">Mot de passe modifié</h2>
                <p className="text-sm text-slate-500">
                  Votre mot de passe a été réinitialisé avec succès. Vous pouvez maintenant vous connecter.
                </p>
                <Button
                  onClick={() => navigate("/auth?mode=login")}
                  className="w-full bg-blue-900 hover:bg-blue-800"
                  data-testid="go-to-login-btn"
                >
                  Se connecter
                </Button>
              </div>
            </CardContent>
          )}
        </Card>

        <div className="tricolore-stripe mt-8 rounded"></div>
      </div>
    </div>
  );
};

export default ForgotPassword;
